import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { guardiaOspite } from '@/services/guardiaOspite';
import { guardiaScritturaNews } from '@/services/guardiaScritturaNews';
import { getLogtoAccessToken } from '@/lib/logto';
import type {
  ArticleDetail,
  ArticleListParams,
  ArticleSummary,
  FeedParams,
  NewsApiResponse,
  NewsTag,
  NewsTagWithChildren,
  NewsUserPreference,
  PaginatedData,
  PreferencesUpdateBody,
  TagArticlesData,
  TagArticlesParams,
  TagListParams,
  TrendingParams,
  VoteResult,
} from '@/types/newsService';

/**
 * QNews API client. Talks to qnews.qlash.gg (proxied through Vite in dev as
 * `/qnews-api`). Auth: qnews-api validates the Logto access token against
 * the same JWKS as the Laravel monolith — no token exchange needed.
 *
 * Ported 1:1 from mobile-app/src/services/newsApi.ts: same endpoints, same
 * payload shapes, same cache tag scheme. The vote/view mutations invalidate
 * the article cache under both slug and `_id` so the detail view refreshes
 * immediately after a vote.
 */
export const newsApi = createApi({
  reducerPath: 'newsApi',
  tagTypes: [
    'NewsArticles',
    'NewsArticle',
    'NewsTags',
    'NewsPreferences',
    'NewsTrending',
    'NewsFeed',
  ],
  // Le scritture escono solo da dove e' lecito (`lib/newsScrittura.ts`): fuori dalla produzione, verso la QNews di produzione,
  // nessuna mutation tocca la rete. Il cancello e' il PIU' ESTERNO, cosi' nessun altro strato vede la richiesta bloccata.
  baseQuery: guardiaScritturaNews(guardiaOspite('qnews', fetchBaseQuery({
    baseUrl: env.newsApiUrl,
    prepareHeaders: async (headers) => {
      const token = await getLogtoAccessToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Accept', 'application/json');
      headers.set('Content-Type', 'application/json');
      headers.set('platform', 'telegram');
      return headers;
    },
  }))),
  endpoints: (builder) => ({
    // --- PUBLIC QUERIES ---

    getArticles: builder.query<PaginatedData<ArticleSummary>, ArticleListParams>({
      query: (params) => ({
        url: '/qnews',
        params,
      }),
      transformResponse: (response: NewsApiResponse<PaginatedData<ArticleSummary>>) =>
        response.data,
      providesTags: ['NewsArticles'],
    }),

    getFeed: builder.query<PaginatedData<ArticleSummary>, FeedParams>({
      query: (params) => ({
        url: '/qnews/feed',
        params,
      }),
      transformResponse: (response: NewsApiResponse<PaginatedData<ArticleSummary>>) =>
        response.data,
      providesTags: ['NewsFeed'],
    }),

    getTrending: builder.query<ArticleSummary[], TrendingParams>({
      query: (params) => ({
        url: '/qnews/trending',
        params,
      }),
      transformResponse: (response: NewsApiResponse<ArticleSummary[]>) => response.data,
      providesTags: ['NewsTrending'],
    }),

    getArticleBySlug: builder.query<ArticleDetail, { slug: string; locale?: string }>({
      query: ({ slug, locale }) => ({
        url: `/qnews/${slug}`,
        params: locale ? { locale } : undefined,
      }),
      transformResponse: (response: NewsApiResponse<ArticleDetail>) => response.data,
      // Provide tags under both slug and `_id` so vote/view mutations (which
      // only know the Mongo id) can invalidate this query.
      providesTags: (result, _error, { slug }) => {
        const tags: { type: 'NewsArticle'; id: string }[] = [
          { type: 'NewsArticle', id: slug },
        ];
        if (result?._id) tags.push({ type: 'NewsArticle', id: result._id });
        return tags;
      },
    }),

    getTags: builder.query<NewsTag[], TagListParams | void>({
      query: (params) => ({
        url: '/tags',
        params: params || undefined,
      }),
      transformResponse: (response: NewsApiResponse<NewsTag[]>) => response.data,
      providesTags: ['NewsTags'],
    }),

    getTagBySlug: builder.query<NewsTagWithChildren, string>({
      query: (slug) => ({
        url: `/tags/${slug}`,
      }),
      transformResponse: (response: NewsApiResponse<NewsTagWithChildren>) => response.data,
    }),

    getTagArticles: builder.query<TagArticlesData<ArticleSummary>, TagArticlesParams>({
      query: ({ slug, ...params }) => ({
        url: `/tags/${slug}/articles`,
        params,
      }),
      transformResponse: (response: NewsApiResponse<TagArticlesData<ArticleSummary>>) =>
        response.data,
    }),

    getPreferences: builder.query<NewsUserPreference, void>({
      query: () => ({
        url: '/preferences',
      }),
      transformResponse: (response: NewsApiResponse<NewsUserPreference>) => response.data,
      providesTags: ['NewsPreferences'],
    }),

    // --- MUTATIONS ---

    castVote: builder.mutation<VoteResult, { articleId: string; value: 1 | -1 }>({
      query: ({ articleId, value }) => ({
        url: `/qnews/${articleId}/vote`,
        method: 'POST',
        body: { value },
      }),
      transformResponse: (response: NewsApiResponse<VoteResult>) => response.data,
      invalidatesTags: (_result, _error, { articleId }) => [
        { type: 'NewsArticle', id: articleId },
      ],
    }),

    removeVote: builder.mutation<VoteResult, string>({
      query: (articleId) => ({
        url: `/qnews/${articleId}/vote`,
        method: 'DELETE',
      }),
      transformResponse: (response: NewsApiResponse<VoteResult>) => response.data,
      invalidatesTags: (_result, _error, articleId) => [
        { type: 'NewsArticle', id: articleId },
      ],
    }),

    trackView: builder.mutation<
      void,
      { articleId: string; dwellTimeMs?: number; shared?: boolean }
    >({
      query: ({ articleId, ...body }) => ({
        url: `/qnews/${articleId}/view`,
        method: 'POST',
        body,
      }),
    }),

    updatePreferences: builder.mutation<NewsUserPreference, PreferencesUpdateBody>({
      query: (body) => ({
        url: '/preferences',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: NewsApiResponse<NewsUserPreference>) => response.data,
      invalidatesTags: ['NewsPreferences', 'NewsFeed'],
    }),
  }),
});

export const {
  useGetArticlesQuery,
  useGetFeedQuery,
  useGetTrendingQuery,
  useGetArticleBySlugQuery,
  useGetTagsQuery,
  useGetTagBySlugQuery,
  useGetTagArticlesQuery,
  useGetPreferencesQuery,
  useCastVoteMutation,
  useRemoveVoteMutation,
  useTrackViewMutation,
  useUpdatePreferencesMutation,
} = newsApi;
